<template>
  <div>
    <ListView
      ref="listView"
      :isFluid="true"
      title="同意紀錄"
      @reload="reload"
      :search.sync="search"
      :data="data"
      :page.sync="page"
      :header="header"
      :enableCreate="false"
      search-box-placeholder="搜尋電話 / 事件 / 來源"
      @navDetails="actionDetails"
    >
      <template v-slot:searchBox>
        <v-row dense>
          <v-col cols="12" sm="6" md="2">
            <v-select
              v-model="searchField"
              :items="searchFieldOptions"
              dense
              outlined
              hide-details
              label="搜尋欄位"
              @change="onSearchFieldChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-select
              v-if="searchField === 'source'"
              v-model="searchVal"
              :items="sourceSearchOptions"
              dense
              outlined
              hide-details
              label="來源"
              clearable
            />
            <v-select
              v-else-if="searchField === 'eventType'"
              v-model="searchVal"
              :items="eventTypeSearchOptions"
              dense
              outlined
              hide-details
              label="事件類型"
              clearable
            />
            <v-text-field
              v-else
              v-model="searchVal"
              dense
              outlined
              hide-details
              :placeholder="searchFieldLabel"
              prepend-inner-icon="mdi-magnify"
              clearable
            />
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="dateStartMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateStart"
                  label="建立日起"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="dateStart = null"
                />
              </template>
              <v-date-picker
                v-model="dateStart"
                no-title
                scrollable
                @input="dateStartMenu = false"
              />
            </v-menu>
          </v-col>
          <v-col cols="12" sm="6" md="2">
            <v-menu
              v-model="dateEndMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="dateEnd"
                  label="建立日迄"
                  prepend-inner-icon="mdi-calendar"
                  readonly
                  dense
                  outlined
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="dateEnd = null"
                />
              </template>
              <v-date-picker
                v-model="dateEnd"
                no-title
                scrollable
                @input="dateEndMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </template>

      <template v-slot:item.eventType="{ item }">
        {{ eventTypeLabel(item.eventType) }}
      </template>

      <template v-slot:item.source="{ item }">
        {{ sourceLabel(item.source) }}
      </template>

      <template v-slot:item.consentTimestamp="{ item }">
        {{ item.consentTimestamp | moment('YYYY-MM-DD HH:mm') }}
      </template>

      <template v-slot:item.optOutTimestamp="{ item }">
        {{ item.optOutTimestamp | moment('YYYY-MM-DD HH:mm') }}
      </template>

      <template v-slot:item.createdAt="{ item }">
        {{ item.createdAt | moment('YYYY-MM-DD HH:mm') }}
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn icon small color="primary" @click.stop="actionDetails(item)">
          <v-icon small>mdi-eye</v-icon>
        </v-btn>
      </template>
    </ListView>

    <OptLogDetailsDialog v-model="detailDialog" :id="detailId" />
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { RESTFUL } from '@/data/constants';
import listViewPageMixin from '@/services/listViewPageMixin';
import OptLogDetailsDialog from '@/components/OptLog/OptLogDetailsDialog';

export default {
  name: 'OptLog',
  components: {
    OptLogDetailsDialog,
  },
  data() {
    return {
      limit: 10,
      header: [
        { text: '電話', value: 'phone' },
        { text: '事件', value: 'eventType' },
        { text: '來源', value: 'source' },
        { text: '同意時間', value: 'consentTimestamp' },
        { text: '拒收時間', value: 'optOutTimestamp' },
        { text: '建立時間', value: 'createdAt' },
        {
          text: '操作',
          value: 'actions',
          sortable: false,
        },
      ],
      populate: [
        { path: 'createdBy', select: ['name'] },
        { path: 'autocompleteProfiles', select: ['name', 'phone', 'type'] },
      ],
      detailDialog: false,
      detailId: '',
      searchField: 'phone',
      searchFieldOptions: [
        { text: '電話', value: 'phone' },
        { text: '事件類型', value: 'eventType' },
        { text: '來源', value: 'source' },
      ],
      sourceSearchOptions: [
        { text: 'WhatsApp', value: 'whatsapp' },
        { text: '手動', value: 'manual' },
      ],
      eventTypeSearchOptions: [
        { text: '同意', value: 'optIn' },
        { text: '拒收', value: 'optOut' },
        { text: '移除拒收', value: 'optOutRemoved' },
      ],
      dateStart: null,
      dateEnd: null,
      dateStartMenu: false,
      dateEndMenu: false,
      sort: { createdAt: -1 },
    };
  },
  mixins: [listViewPageMixin],
  computed: {
    restfulURL() {
      return RESTFUL.optLog.list;
    },
    searchFieldLabel() {
      const field = this.searchFieldOptions.find(f => f.value === this.searchField);
      return field ? `請輸入${field.text}` : '請輸入搜尋內容';
    },
    searchVal: {
      get() {
        return this.search;
      },
      set(val) {
        this.search = val;
      },
    },
    searchPaths() {
      return this.searchField ? [this.searchField] : ['phone', 'eventType', 'source'];
    },
    mergeParams() {
      const params = {};
      if (this.dateStart) {
        params.startDate = dayjs(this.dateStart).startOf('day').toISOString();
      }
      if (this.dateEnd) {
        params.endDate = dayjs(this.dateEnd).endOf('day').toISOString();
      }
      return Object.keys(params).length ? params : null;
    },
  },
  methods: {
    onSearchFieldChange() {
      this.search = '';
    },
    sourceLabel(source) {
      if (source === 'manual') return '手動';
      if (source === 'whatsapp') return 'WhatsApp';
      return source || '';
    },
    eventTypeLabel(eventType) {
      if (eventType === 'optIn') return '同意';
      if (eventType === 'optOut') return '拒收';
      if (eventType === 'optOutRemoved') return '移除拒收';
      return eventType || '';
    },
    actionDetails(item) {
      this.detailId = item._id;
      this.detailDialog = true;
    },
  },
};
</script>
